import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { setCurrentUser } from '../../ducks/user.duck';
import { apiBaseUrl } from '../../util/api';

// ================ Async Thunks ================ //

//////////////////
// Upload Image //
//////////////////
export const uploadImageThunk = createAsyncThunk(
  'ProfileSettingsPage/uploadImage',
  ({ id, file }, { rejectWithValue, extra: sdk }) => {
    const bodyParams = {
      image: file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        return { id, uploadedImage };
      })
      .catch(e => {
        return rejectWithValue({ id, error: storableError(e) });
      });
  }
);
// Backward compatible wrapper for the uploadImage thunk
export const uploadImage = actionPayload => dispatch => {
  return dispatch(uploadImageThunk(actionPayload));
};

////////////////////
// Update Profile //
////////////////////
export const updateProfileThunk = createAsyncThunk(
  'ProfileSettingsPage/updateProfile',
  (actionPayload, { dispatch, rejectWithValue, extra: sdk, getState }) => {
    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    // Get current user data before update to detect changes
    const state = getState();
    const currentUser = state.user.currentUser;
    const oldOrganicCertificate = currentUser?.attributes?.profile?.publicData?.organicCertificate;
    const newOrganicCertificate = actionPayload?.publicData?.organicCertificate;

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(async response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const updatedUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(setCurrentUser(updatedUser));

        // Send email notification if organic certificate changed
        if (
          newOrganicCertificate &&
          newOrganicCertificate !== oldOrganicCertificate
        ) {
          try {
            const userId = updatedUser.id.uuid;
            const userEmail = updatedUser.attributes.email;
            const firstName = updatedUser.attributes.profile.firstName || '';
            const lastName = updatedUser.attributes.profile.lastName || '';
            const userName = `${firstName} ${lastName}`.trim() || 'Usuario sin nombre';
            const marketplaceUrl = window.location.origin;
            const profileUrl = `${marketplaceUrl}/u/${userId}`;
            
            // Get additional user info from profile
            const publicData = updatedUser.attributes.profile.publicData || {};
            const protectedData = updatedUser.attributes.profile.protectedData || {};
            const additionalInfo = {
              farmName: publicData.farmName,
              address: publicData.address?.selectedPlace?.address,
              city: publicData.city,
              province: publicData.province,
              phoneNumber: protectedData.phoneNumber,
              certificateInfo: publicData.certificateInfo,
              operationArea: publicData.operationArea?.selectedPlace?.address,
            };

            console.log('📧 Sending organic certificate email notification...', {
              userId,
              userEmail,
              oldValue: oldOrganicCertificate,
              newValue: newOrganicCertificate,
            });

            // Call backend API to send email
            const url = `${apiBaseUrl()}/api/send-organic-certificate-notification`;
            const emailResponse = await fetch(url, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                userId,
                userEmail,
                userName,
                organicCertificateStatus: newOrganicCertificate,
                profileUrl,
                additionalInfo,
              }),
            });

            if (!emailResponse.ok) {
              const errorData = await emailResponse.json();
              console.error('❌ Email API error:', errorData);
            } else {
              const result = await emailResponse.json();
              console.log('✅ Organic certificate email notification sent:', result);
            }
          } catch (emailError) {
            console.error('❌ Failed to send organic certificate email:', emailError);
            // Don't fail the whole update if email fails
          }
        }

        return response;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrapper for the updateProfile thunk
export const updateProfile = actionPayload => dispatch => {
  return dispatch(updateProfileThunk(actionPayload));
};

// ================ Slice ================ //

const profileSettingsPageSlice = createSlice({
  name: 'ProfileSettingsPage',
  initialState: {
    image: null,
    uploadImageError: null,
    uploadInProgress: false,
    updateInProgress: false,
    updateProfileError: null,
  },
  reducers: {
    clearUpdatedForm: state => {
      state.updateProfileError = null;
      state.uploadImageError = null;
    },
  },
  extraReducers: builder => {
    builder
      // uploadImage cases
      .addCase(uploadImageThunk.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.image = { id, file };
        state.uploadInProgress = true;
        state.uploadImageError = null;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action) => {
        const { id, uploadedImage } = action.payload;
        const { file } = state.image || {};
        state.image = { id, imageId: uploadedImage.id, file, uploadedImage };
        state.uploadInProgress = false;
      })
      .addCase(uploadImageThunk.rejected, (state, action) => {
        state.image = null;
        state.uploadInProgress = false;
        state.uploadImageError = action.payload.error;
      })
      // updateProfile cases
      .addCase(updateProfileThunk.pending, state => {
        state.updateInProgress = true;
        state.updateProfileError = null;
      })
      .addCase(updateProfileThunk.fulfilled, state => {
        state.image = null;
        state.updateInProgress = false;
      })
      .addCase(updateProfileThunk.rejected, (state, action) => {
        state.image = null;
        state.updateInProgress = false;
        state.updateProfileError = action.payload;
      });
  },
});

export const { clearUpdatedForm } = profileSettingsPageSlice.actions;
export default profileSettingsPageSlice.reducer;
